import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar, MapPin, User, Loader2, PackageOpen, Star } from "lucide-react";
import RescheduleDialog from "@/components/RescheduleDialog";
import RatingDialog from "@/components/RatingDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const STATUS_STYLE = {
  confirmed: "bg-emerald-500/15 text-emerald-600",
  completed: "bg-blue-500/15 text-blue-600",
  cancelled: "bg-destructive/15 text-destructive",
  pending_payment: "bg-amber-500/15 text-amber-600",
};

export default function MyBookings() {
  const [bookings, setBookings] = useState(null);
  const [availMap, setAvailMap] = useState({});

  const load = () => api.get("/bookings/mine").then((r) => setBookings(r.data));
  useEffect(() => {
    load();
    api.get("/purohits").then((r) => {
      const m = {};
      r.data.forEach((p) => { m[p.id] = p.availability || {}; });
      setAvailMap(m);
    });
  }, []);

  const cancel = async (id) => {
    try { await api.post(`/bookings/${id}/cancel`); toast.success("Booking cancelled"); load(); }
    catch { toast.error("Could not cancel"); }
  };

  if (bookings === null) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-12" data-testid="my-bookings-page">
      <h1 className="font-serif text-3xl lg:text-4xl font-bold mb-2">My Bookings</h1>
      <p className="text-muted-foreground mb-8">Track your worships and rituals — upcoming and past.</p>

      {bookings.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center" data-testid="bookings-empty">
          <PackageOpen className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">You have no bookings yet.</p>
          <Link to="/divine-bless"><Button className="mt-4 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">Book your first worship</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`} className="rounded-2xl border border-border bg-card p-5 flex flex-col sm:flex-row gap-4">
              <img src={b.service_image} alt="" className="h-24 w-full sm:w-28 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-lg font-semibold leading-tight">{b.service_name}</h3>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">{b.id}</p>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${STATUS_STYLE[b.status] || "bg-secondary"}`}>{b.status.replace("_", " ")}</span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-accent" /> {b.mandir_name}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-accent" /> {b.date} · {b.time}</span>
                  <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-accent" /> {b.purohit_name}</span>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <span className="font-serif font-bold text-primary">{inr(b.total)}</span>
                  {b.status === "confirmed" && (
                    <div className="flex gap-2">
                      <RescheduleDialog booking={b} availability={availMap[b.purohit_id]} onDone={load} />
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="outline" size="sm" data-testid={`cancel-${b.id}`} className="rounded-full text-destructive border-destructive/40 hover:bg-destructive hover:text-white">Cancel</Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Cancel this booking?</AlertDialogTitle><AlertDialogDescription>Your booking for {b.service_name} will be cancelled. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel><AlertDialogAction onClick={() => cancel(b.id)} className="bg-destructive hover:bg-destructive/90">Yes, cancel</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                  {b.status === "completed" && (
                    b.rated ? (
                      <span className="flex items-center gap-1 text-sm" data-testid={`rated-${b.id}`}>
                        {[1,2,3,4,5].map((n) => <Star key={n} className={`h-4 w-4 ${b.rating >= n ? "fill-accent text-accent" : "text-muted-foreground/30"}`} />)}
                      </span>
                    ) : (
                      <RatingDialog booking={b} onDone={load} />
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
